import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { HeartIcon, MapPinIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import AmenityIcons from './AmenityIcons';
import ImageCarousel from './ImageCarousel';
import DesignTokens from '../../styles/DesignTokens';
import { normalizeImageUrl } from '../../utils/urlUtils';
import OptimizedImage from './OptimizedImage';

const CardContainer = styled(motion.div)`
  position: relative;
  background: white;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  cursor: pointer;
  transition: all ${DesignTokens.transition.slow} ${DesignTokens.transition.easing};
  box-shadow: ${DesignTokens.shadow.sm};
  border: 1px solid ${props => props.theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  &:hover {
    box-shadow: ${DesignTokens.shadow.xl};
    transform: translateY(-4px);
    border-color: ${props => props.theme.colors.primaryLight};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: ${props => props.theme.colors.gradientPrimary};
  flex-shrink: 0;
  min-height: 0;
`;

const PlaceImage = styled(OptimizedImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${DesignTokens.transition.slower} ${DesignTokens.transition.easing};

  ${CardContainer}:hover & {
    transform: scale(1.1);
  }
  
  /* Responsive image loading */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${DesignTokens.typography.fontSize['5xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
  padding: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${CardContainer}:hover & {
    opacity: 1;
  }
`;

const FavoriteButton = styled(motion.button)`
  position: absolute;
  top: ${DesignTokens.spacing.md};
  right: ${DesignTokens.spacing.md};
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: ${DesignTokens.borderRadius.full};
  width: 75px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${DesignTokens.transition.normal} ${DesignTokens.transition.easing};
  z-index: 10;
  backdrop-filter: blur(10px);
  box-shadow: ${DesignTokens.shadow.md};

  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: ${DesignTokens.shadow.lg};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${props => props.isFavorite ? props.theme.colors.tertiary : props.theme.colors.text};
    transition: all ${DesignTokens.transition.normal} ease;
  }
`;

const Badge = styled(motion.div)`
  position: absolute;
  top: ${DesignTokens.spacing.md};
  left: ${DesignTokens.spacing.md};
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  z-index: 10;
  box-shadow: ${DesignTokens.shadow.md};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CardContent = styled.div`
  padding: ${DesignTokens.spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${DesignTokens.spacing.sm};
  gap: ${DesignTokens.spacing.sm};
`;

const Title = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.base};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  line-height: ${DesignTokens.typography.lineHeight.tight};
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  flex-shrink: 0;
  background: ${props => props.theme.colors.accentLight};
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  border-radius: ${DesignTokens.borderRadius.full};

  svg {
    width: 14px;
    height: 14px;
    fill: ${props => props.theme.colors.accent};
    color: ${props => props.theme.colors.accent};
  }
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  margin-bottom: ${DesignTokens.spacing.sm};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${props => props.theme.colors.primary};
  }
`;

const Description = styled.p`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin-bottom: ${DesignTokens.spacing.md};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AmenitiesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.sm};
  margin-top: ${DesignTokens.spacing.sm};
  padding-top: ${DesignTokens.spacing.sm};
  border-top: 1px solid ${props => props.theme.colors.borderLight};
`;

const CategoryTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.secondaryLight};
  color: ${props => props.theme.colors.secondary};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  text-transform: capitalize;
`;

const ExploreButton = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  color: ${props => props.theme.colors.primary};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  font-size: ${DesignTokens.typography.fontSize.sm};
  margin-top: ${DesignTokens.spacing.sm};
  padding: ${DesignTokens.spacing.sm} 0;
  transition: all ${DesignTokens.transition.normal} ease;

  ${CardContainer}:hover & {
    color: ${props => props.theme.colors.primaryHover};
    gap: ${DesignTokens.spacing.md};
  }
`;

function PlaceCard({ place, index = 0, showSingleImage = false, source = null }) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Capture place data immediately to avoid closure issues
  const placeId = place?.id;
  const placeName = place?.name;
  const placeCountry = place?.country;
  
  // Debug logging for specific places
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && placeName?.toLowerCase().includes('petra')) {
      console.log('PlaceCard rendered for Petra:', { placeId, placeName, placeCountry, place });
    }
  }, [placeId, placeName, placeCountry, place]);

  const displayName = useMemo(() => {
    if (place?.name && !/^Q\\d+$/.test(place.name)) {
      return place.name;
    }
    if (place?.wikipediaUrl) {
      const idx = place.wikipediaUrl.lastIndexOf('/');
      if (idx >= 0 && idx < place.wikipediaUrl.length - 1) {
        const slug = place.wikipediaUrl.substring(idx + 1);
        try {
          return decodeURI(slug).replace(/_/g, ' ');
        } catch {
          return slug.replace(/_/g, ' ');
        }
      }
    }
    return place?.name || 'Unknown';
  }, [place]);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  // Generate image URL or use placeholder - normalize to fix double-encoding
  const rawImageUrl = place.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (place.id || 0)}?w=400&h=300&fit=crop`;
  const imageUrl = normalizeImageUrl(rawImageUrl) || rawImageUrl;
  
  // Normalize images array as well
  const normalizedImages = useMemo(() => {
    if (!place.images || !Array.isArray(place.images)) return null;
    return place.images
      .map(img => normalizeImageUrl(img))
      .filter(img => img !== null);
  }, [place.images]);

  // Calculate rating (mock for now)
  const rating = (4.5 + (place.id % 5) * 0.1).toFixed(1);

  // Determine badge based on category
  const getBadge = () => {
    if (place.gamesCount > 0) {
      return { text: `${place.gamesCount} Game${place.gamesCount > 1 ? 's' : ''}`, icon: SparklesIcon, color: 'purple' };
    }
    if (place.category === 'historical') {
      return { text: 'Historical', icon: null, color: 'primary' };
    }
    return null;
  };

  const badge = getBadge();
  
  // Add source parameter to link if provided (e.g., ?source=unesco for heritage pages)
  const linkPath = source 
    ? `/places/${placeId}?source=${source}`
    : `/places/${placeId}`;

  // Create click handler with captured data (must be before early return)
  const handleCardClick = useCallback((e) => {
    // Ensure we're navigating to the correct place
    if (process.env.NODE_ENV === 'development') {
      console.log('PlaceCard clicked:', { 
        placeId, 
        placeName, 
        placeCountry,
        linkPath,
        actualPlace: place 
      });
    }
    
    // Double-check the place ID before navigation
    if (place?.id !== placeId) {
      console.error('PlaceCard ID mismatch!', {
        expectedId: placeId,
        actualPlaceId: place?.id,
        placeName,
        place
      });
    }
  }, [placeId, placeName, placeCountry, linkPath, place]);
  
  // Safety check: if placeId is invalid, don't render the link
  if (!placeId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('PlaceCard: place.id is missing', { place, placeId, placeName });
    }
    return null;
  }

  return (
    <Link 
      to={linkPath} 
      style={{ textDecoration: 'none' }}
      onClick={handleCardClick}
    >
      <CardContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          duration: 0.4, 
          delay: index * 0.05,
          ease: [0.4, 0, 0.2, 1]
        }}
        whileHover={{ y: -4 }}
      >
        <ImageContainer>
          {!imageError && !showSingleImage && ((normalizedImages && normalizedImages.length > 0) || imageUrl) ? (
            <>
              <ImageCarousel
                images={normalizedImages}
                mainImage={imageUrl}
                autoPlay={true}
                interval={4000}
              />
              <ImageOverlay>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: 500 }}>
                  Click to explore
                </div>
              </ImageOverlay>
            </>
          ) : !imageError ? (
            <>
              <PlaceImage
                src={showSingleImage
                  ? (imageUrl || (normalizedImages && normalizedImages[0]) || imageUrl)
                  : imageUrl
                }
                alt={place.name || 'Famous Place'}
                width={400}
                height={300}
                lazy={true}
                quality={85}
                onError={() => setImageError(true)}
              />
              <ImageOverlay>
                <div style={{ color: 'white', fontSize: '0.875rem', fontWeight: 500 }}>
                  Click to explore
                </div>
              </ImageOverlay>
            </>
          ) : (
            <ImagePlaceholder>
              {place.name?.charAt(0) || '?'}
            </ImagePlaceholder>
          )}
          
          {badge && (
            <Badge
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.05 + 0.2 }}
            >
              {badge.icon && <badge.icon />}
              <span>{badge.text}</span>
            </Badge>
          )}

          <FavoriteButton
            onClick={handleFavoriteClick}
            isFavorite={isFavorite}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {isFavorite ? (
              <HeartSolidIcon />
            ) : (
              <HeartIcon />
            )}
          </FavoriteButton>
        </ImageContainer>

        <CardContent>
          <HeaderRow>
            <Title>{displayName}</Title>
            <Rating>
              <StarSolidIcon />
              <span>{rating}</span>
            </Rating>
          </HeaderRow>

          <Location>
            <MapPinIcon />
            <span>
              {place.city ? `${place.city}, ` : ''}{place.country}
            </span>
          </Location>
          
          {place.description && (
            <Description>{place.description}</Description>
          )}

          <AmenitiesRow>
            {place.timeToComplete && (
              <AmenityIcons.Duration minutes={place.timeToComplete.split('-')[0].trim()} />
            )}
            {place.ageRange && (
              <AmenityIcons.AgeGroup ageRange={place.ageRange} />
            )}
            {place.category && (
              <CategoryTag>{place.category.replace('-', ' ')}</CategoryTag>
            )}
          </AmenitiesRow>

          <ExploreButton
            whileHover={{ x: 4 }}
          >
            Explore <ArrowRightIcon style={{ width: '14px', height: '14px' }} />
          </ExploreButton>
        </CardContent>
      </CardContainer>
    </Link>
  );
}

export default PlaceCard;
